import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Container, Row, Col } from 'reactstrap';
import moment from 'moment';
import config from '../../../../../config';
import TableBuilder from '../../../../../shared/components/table/TableBuilder';
import { getExportPrefix } from '../../../../../shared/components/table/functions';
import Notify from '../../../../../shared/components/widgets/Notify';
import CardPanel from '../../../../../shared/components/widgets/CardPanel';

export default function DspCreatives() {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const [forceRefresh, setForceRefresh] = useState(moment());
  const [agGrid, setAgGrid] = useState(null);
  const [updatedRowCount, setUpdatedRowCount] = useState(0);
  const [buildCount, setBuildCount] = useState(0);
  const [originalData, setOriginalData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [managerAccountId, setManagerAccountId] = useState('201');
  // function jsonCellRenderer(params) {
  // // Hold  cellRenderer: (params) => <pre style={{ fontFamily: 'monospace', margin: 0, padding: 0, whiteSpace: 'pre-wrap' }}>${JSON.stringify(JSON.parse(params.value), null, 2)}</pre>
  //   return `<pre style="font-family: monospace; margin: 0; padding: 0; white-space: pre-wrap;">${JSON.stringify(JSON.parse(params.value), null, 2)}</pre>`;
  // }

  const resetRow = (orgRowObj) => {
    agGrid.gridApi.forEachNode((rowNode) => {
      if (orgRowObj.id === rowNode.data.id) {
        rowNode.setData(orgRowObj);
        setUpdatedRowCount(curentCount => curentCount - 1);
      }
    });
  };

  const removeRow = (rowData) => {
    agGrid.gridApi.applyTransaction({ remove: [rowData.data] });
    setUpdatedRowCount(curentCount => curentCount - 1);
  };

  const submitDspC = () => {
    setSubmitting(true);
    const dspCreativesUrl = `${config.serverUrl}/management/dsp/creatives`;

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

    const saveData = [];
    // Build changes
    agGrid.gridApi.forEachNode((rowNode) => {
      if (rowNode.data.change || rowNode.data.id === 0) {
        const { change, ...rest } = rowNode.data;
        saveData.push({ ...rest }); // Remove fields we don't care about and add to changedRows
      }
    });

    const requestOptions = {
      method: 'POST',
      headers: headerWithAuth,
      body: JSON.stringify({
        accountId: selectedSeller.value,
        saveData,
      }),
    };

    fetch(dspCreativesUrl, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      }).then(() => {
        Notify.show('primary', 'Success', 'Creatives have been saved!');
        setUpdatedRowCount(0);
        setForceRefresh(moment());
        setSubmitting(false);
      }).catch(() => {
        Notify.show('danger', 'Error', 'We were unable to save your Creatives.');
        setForceRefresh(moment());
        setSubmitting(false);
      });
  };

  const submitBuild = () => {
    if (buildCount > 0) {
      setSubmitting(true);
      const dspCreativeBuildUrl = `${config.serverUrl}/management/dsp/creatives/build?accountId=${selectedSeller.value}&managerAccountId=${managerAccountId}`;

      const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

      const requestOptions = {
        method: 'GET',
        headers: headerWithAuth,
      };

      fetch(dspCreativeBuildUrl, requestOptions)
        .then((results) => {
          if (!results.ok) {
            throw Error(results.statusText);
          }
          return results.json();
        }).then(() => {
          Notify.show('primary', 'Success', 'Creatives Built!');
          setForceRefresh(moment());
          setSubmitting(false);
        }).catch(() => {
          Notify.show('danger', 'Error', 'We were unable to build your Creatives.');
          setForceRefresh(moment());
          setSubmitting(false);
        });
    } else {
      Notify.show('danger', 'Error', 'Nothing set to BUILD');
    }
  };

  const highlightChanges = (row) => {
    if ((row.data?.change && row.value !== row.data.change[row.colDef.field]) || (row.data.id === 0)) {
      return { backgroundColor: '#91BE23' };
    }
    return { backgroundColor: '' };
  }

  const columnDefs = [
    {
      field: 'change',
      headerName: 'CHANGE',
      cellStyle: (params) => (params.value !== '' ? { color: '#91BE23' } : { color: 'black' }),
      cellRenderer: (params) => {
        if (params.value && params.data.id > 0) {
          return <button type="button" className="general_theme" style={{ height: '100%' }} onClick={() => resetRow(params.value)}>Reset Row</button>;
        }
        if (params.data.id === 0) {
          return <button type="button" className="general_theme" style={{ height: '100%' }} onClick={() => removeRow(params)}>Remove Row</button>;
        }
        return ''
      },
      editable: false,
    },
    { field: 'id', headerName: 'ID', editable: false },
    { field: 'accountId', headerName: 'Account ID', editable: false },
    { field: 'dspAccountId', headerName: 'DSP Advertiser ID', cellStyle: params => highlightChanges(params) },
    { field: 'adCreativeId', headerName: 'AD Creative ID', cellStyle: params => highlightChanges(params) },
    { field: 'name', headerName: 'Name', cellStyle: params => highlightChanges(params) },
    { field: 'country', headerName: 'Country', cellStyle: params => highlightChanges(params) },
    { field: 'language', headerName: 'Language', cellStyle: params => highlightChanges(params) },
    { field: 'formatType', headerName: 'Format Type', cellStyle: params => highlightChanges(params) },
    { field: 'adChoicePosition', headerName: 'AD Choice Position', cellStyle: params => highlightChanges(params) },
    { field: 'autoLocalizationState', headerName: 'auto_localization_state', cellStyle: params => highlightChanges(params) },
    { field: 'categoriesCsv', headerName: 'categories_csv', cellStyle: params => highlightChanges(params) },
    { field: 'externalId', headerName: 'external_id', cellStyle: params => highlightChanges(params) },
    { field: 'attributesCsv', headerName: 'attributes_csv', cellStyle: params => highlightChanges(params) },
    { field: 'accessibilityText', headerName: 'accessibility_text', cellStyle: params => highlightChanges(params) },
    { field: 'additionalHtml', headerName: 'additional_html', cellStyle: params => highlightChanges(params) },
    { field: 'hostedType', headerName: 'Hosted Type', cellStyle: params => highlightChanges(params) },
    { field: 'assetKey', headerName: 'Asset Key', cellStyle: params => highlightChanges(params) },
    { field: 'assetSizesCsv', headerName: 'Asset Sizes CSV', cellStyle: params => highlightChanges(params) },
    { field: 'responsive', headerName: 'Responsive', cellStyle: params => highlightChanges(params) },
    { field: 'applyBorder', headerName: 'Apply Border', cellStyle: params => highlightChanges(params) },
    { field: 'urlDestination', headerName: 'URL Destination', cellStyle: params => highlightChanges(params) },
    { field: 'associatedAdExperience', headerName: 'associated_ad_experience', cellStyle: params => highlightChanges(params) },
    { field: 'enableDeepLinking', headerName: 'enable_deep_linking', cellStyle: params => highlightChanges(params) },
    { field: 'status', headerName: 'Status', cellStyle: params => highlightChanges(params) },
    { field: 'udpateTs', headerName: 'Udpate TS', editable: false },
    { field: 'createTs', headerName: 'Create TS', editable: false },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    editable: true,
  };

  const showDatePicker = false;
  const showExportOptions = true;
  const apiEndPoint = `${config.serverUrl}/management/dsp/creatives`;

  const exportPrefix = getExportPrefix('amz-dsp-creatives', selectedSeller, true);

  const parentAccess = (agGridPointer) => {
    setAgGrid(agGridPointer);
  }

  const parentSetData = (data) => {
    setOriginalData(JSON.parse(JSON.stringify(data)));
  }

  const onRowValueChanged = (cellData) => {
    if (!agGrid) {
      return;
    }
    agGrid.gridApi.forEachNode((rowNode) => {
      if (cellData.data.id === rowNode.data.id) {
        if (!rowNode.data.change) {
          const origData = originalData.find(origRow => origRow.id === cellData.data.id);
          rowNode.setDataValue('change', { ...origData });
          if (cellData.data.id !== 0) {
            setUpdatedRowCount(curentCount => curentCount + 1);
          }
        }
      }
    })
  }

  const getNewRow = () => {
    const newData = {
      id: 0,
      accountId: selectedSeller.value,
      dspAccountId: null,
      adCreativeId: null,
      name: null,
      country: null,
      language: null,
      adChoicePosition: null,
      autoLocalizationState: null,
      categoriesCsv: null,
      accessibilityText: null,
      hostedType: null,
      assetKey: null,
      assetSizesCsv: null,
      responsive: 'FALSE',
      applyBorder: 'TRUE',
      urlDestination: null,
      associatedAdExperience: null,
      enableDeepLinking: null,
      status: 'BUILDING',
      udpateTs: null,
      createTs: null,
    };
    setUpdatedRowCount(curentCount => curentCount + 1);
    return newData;
  }


  useEffect(() => {
    if (agGrid) {
      agGrid.refreshData();
      let calcBuildCount = 0;
      agGrid.gridApi.forEachNode((rowNode) => {
        if (rowNode.data?.status === 'BUILD') {
          calcBuildCount += 1;
        }
        setBuildCount(calcBuildCount);
      });
    }
  }, [forceRefresh]);

  return (
    <Container>
      <Row>
        <TableBuilder
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          apiEndPoint={apiEndPoint}
          showDatePicker={showDatePicker}
          showExportOptions={showExportOptions}
          exportPrefix={exportPrefix}
          parentAccess={parentAccess}
          parentSetData={parentSetData}
          onRowValueChanged={onRowValueChanged}
          showAddRowButton
          getNewRow={getNewRow}
        />
      </Row>
      <Row>
        <CardPanel
          title={'Actions:'}
          autorenewButton={false}
          closeButton={false}
        >
          <Row>
            <Col md="3">
              <div style={{ width: '500px' }}>
                <p className="bold-text">{`Updated Count: ${updatedRowCount}`}</p>
                <br />
                <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => submitDspC()}>Save Creatives</button>
              </div>
            </Col>
            <Col md="3">
              <div style={{ width: '500px' }}>
                <p className="bold-text">{`Build Count: ${buildCount}`}</p>
                <p>Manager Account ID: <input type="text" className="input_basic" id="managerAccountId" value={managerAccountId} onChange={(e) => setManagerAccountId(e.target.value)} /></p>
                <span className="hint-text">201=Americas, 202=EMEA, 203=APAC</span>
                <br />
                <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => submitBuild()}>Build Creatives</button>
              </div>
            </Col>
          </Row>
        </CardPanel>
      </Row>
    </Container>
  );
}
